import type { FC, ReactNode } from 'react'

import Image from 'next/image'
import { ArrowRight, BadgeCheck, Building2, Lightbulb } from 'lucide-react'
import { selectEnquiryRole } from '@/lib/enquiry'
import Container from '@/components/landing/container'
import StarRating from '@/components/landing/star-rating'

import { HERO_STATS, LINKS } from '@/constants/landing'

const enter = 'animate-in fade-in slide-in-from-bottom-4 duration-700 ease-out fill-mode-both motion-reduce:animate-none'

const HeroVisual: FC = (): ReactNode => {
    return (
        <div className='relative mx-auto w-full max-w-[26rem] sm:max-w-[30rem] lg:mr-0 lg:max-w-[32rem]'>
            <svg
                aria-hidden='true'
                viewBox='0 0 120 60'
                className='absolute -top-[4.25rem] left-1/2 h-16 w-32 -translate-x-1/2 text-brand-orange'
            >
                <g fill='none' stroke='currentColor' strokeWidth='6' strokeLinecap='round'>
                    <path d='M60 5v17' />
                    <path d='M31 12l7 14' />
                    <path d='M89 12l-7 14' />
                    <path d='M8 30l14 8' />
                    <path d='M112 30l-14 8' />
                </g>
            </svg>

            <div aria-hidden='true' className='absolute inset-0 translate-x-3 translate-y-3 rounded-t-[999px] rounded-b-[2.5rem] bg-primary sm:translate-x-5 sm:translate-y-5' />

            <div aria-hidden='true' className='absolute -left-6 top-1/3 hidden size-20 rounded-full border-[10px] border-brand-teal/30 sm:block' />

            <div className='relative aspect-[4/5] overflow-hidden rounded-t-[999px] rounded-b-[2.5rem] border-[6px] border-white bg-sand shadow-2xl shadow-brand-navy/20'>
                <Image
                    src='/images/hero-student.png'
                    alt="Smiling young woman holding notebooks with a backpack on her shoulder"
                    fill
                    preload
                    sizes='(min-width: 1024px) 32rem, (min-width: 640px) 30rem, 90vw'
                    className='object-cover object-[60%_20%]'
                />
            </div>

            <div className={`${enter} delay-300 absolute right-0 top-[22%] flex items-center gap-3 rounded-2xl bg-white p-2.5 pr-4 shadow-xl ring-1 ring-black/5 sm:-right-8 sm:p-3 sm:pr-5`}>
                <span className='grid size-9 place-items-center rounded-xl bg-accent text-brand-teal-ink sm:size-10'>
                    <BadgeCheck className='size-5' aria-hidden='true' />
                </span>

                <span className='leading-tight'>
                    <span className='block text-xs font-semibold text-foreground sm:text-sm'>Placement assistance</span>
                    <span className='block text-[11px] text-muted-foreground sm:text-xs'>with every course</span>
                </span>
            </div>

            <div className={`${enter} delay-500 absolute left-0 top-[56%] flex items-center gap-3 rounded-full bg-white p-1.5 pr-4 shadow-xl ring-1 ring-black/5 sm:-left-10 sm:pr-5`}>
                <span className='relative size-11 overflow-hidden rounded-full bg-sand sm:size-12'>
                    <Image src='/images/learner-campus.png' alt='' fill sizes='48px' className='object-cover object-top' />
                </span>

                <span className='leading-tight'>
                    <span className='block text-xs font-semibold text-foreground sm:text-sm'>Live expert sessions</span>
                    <span className='block text-[11px] text-muted-foreground sm:text-xs'>Learn from industry leaders</span>
                </span>
            </div>

            <div className={`${enter} delay-700 absolute -bottom-6 right-4 rounded-2xl bg-brand-navy px-4 py-3.5 text-white shadow-xl sm:right-10 sm:px-5 sm:py-4`}>
                <StarRating value={4.5} trackClassName='text-white/20' />
                <p className='mt-1.5 text-sm font-semibold'>4.5 out of 5</p>
                <p className='text-xs text-white/70'>average learner rating</p>
            </div>
        </div>
    )
}

const HeroSection: FC = (): ReactNode => {
    return (
        <section id='hero' aria-labelledby='hero-title' className='relative isolate overflow-hidden'>
            <div aria-hidden='true' className='pointer-events-none absolute inset-0 -z-10'>
                <div className='absolute -right-40 -top-48 size-[40rem] rounded-full bg-brand-teal/10 blur-3xl' />
                <div className='absolute -bottom-56 -left-40 size-[34rem] rounded-full bg-brand-orange/10 blur-3xl' />
                <div className='absolute inset-0 bg-[radial-gradient(circle_at_1px_1px,rgba(43,58,143,0.12)_1px,transparent_0)] bg-[length:28px_28px] [mask-image:linear-gradient(to_bottom,black,transparent_70%)]' />
            </div>

            <Container className='grid items-center gap-24 pb-24 pt-8 sm:pt-12 lg:grid-cols-12 lg:gap-10 lg:pb-28 lg:pt-20'>
                <div className='lg:col-span-6'>
                    <p className={`${enter} inline-flex items-center gap-2 rounded-full border border-brand-orange/30 bg-white/80 px-3.5 py-1.5 text-sm font-semibold text-brand-orange-ink shadow-sm`}>
                        <Lightbulb className='size-4' aria-hidden='true' />
                        Your gateway to the corporate world
                    </p>

                    <h1
                        id='hero-title'
                        className={`${enter} delay-100 mt-6 text-[2.75rem] font-bold leading-[1.04] tracking-tight text-foreground sm:text-6xl lg:text-[4.25rem] xl:text-7xl`}
                    >
                        From learning
                        {' '}
                        <br className='hidden sm:block' />
                        to
                        {' '}
                        <span className='relative inline-block text-primary'>
                            earning.
                            <svg
                                aria-hidden='true'
                                viewBox='0 0 300 24'
                                preserveAspectRatio='none'
                                className='absolute -bottom-2 left-0 h-3 w-full text-brand-orange sm:-bottom-3 sm:h-4'
                            >
                                <path d='M4 17C70 6 170 3 296 11' fill='none' stroke='currentColor' strokeWidth='7' strokeLinecap='round' />
                            </svg>
                        </span>
                    </h1>

                    <p className={`${enter} delay-200 mt-8 max-w-xl text-lg leading-relaxed text-muted-foreground sm:text-xl`}>
                        Job-ready training for Banking, Automobile and Customer Relationship roles, built for job seekers in India&rsquo;s tier 2 and tier 3 cities. Learn online, train with industry experts and get placement assistance.
                    </p>

                    <div className={`${enter} delay-300 mt-9 flex flex-col gap-3 sm:flex-row sm:items-center`}>
                        <a
                            href='#courses'
                            className='group inline-flex h-13 items-center justify-center gap-2 rounded-full bg-primary px-7 text-base font-semibold text-primary-foreground shadow-[0_14px_30px_-14px_rgba(43,58,143,0.8)] transition-colors hover:bg-brand-navy'
                        >
                            Explore courses
                            <ArrowRight className='size-5 transition-transform group-hover:translate-x-0.5' aria-hidden='true' />
                        </a>

                        <a
                            href='#recruiters'
                            onClick={() => selectEnquiryRole('recruiter')}
                            className='inline-flex h-13 items-center justify-center gap-2 rounded-full border border-border bg-white px-7 text-base font-semibold text-foreground transition-colors hover:border-primary/40 hover:text-primary'
                        >
                            <Building2 className='size-5' aria-hidden='true' />
                            I&rsquo;m hiring
                        </a>
                    </div>

                    <p className={`${enter} delay-300 mt-5 text-sm text-muted-foreground`}>
                        Already learning with us?
                        {' '}
                        <a
                            href={LINKS.learningPortal}
                            target='_blank'
                            rel='noopener noreferrer'
                            className='font-semibold text-primary underline-offset-4 hover:underline'
                        >
                            Go to the learning portal
                            <span className='sr-only'> (opens in a new tab)</span>
                        </a>
                    </p>

                    <dl className={`${enter} delay-500 mt-12 grid max-w-xl grid-cols-3 gap-4 border-t border-border pt-8`}>
                        {HERO_STATS.map(stat => (
                            <div key={stat.label} className='flex flex-col'>
                                <dt className='order-2 mt-1 text-xs leading-snug text-muted-foreground sm:text-sm'>
                                    {stat.label}
                                </dt>

                                <dd className='order-1 font-heading text-2xl font-bold text-foreground sm:text-3xl'>
                                    {stat.value}
                                </dd>
                            </div>
                        ))}
                    </dl>
                </div>

                <div className='lg:col-span-6'>
                    <HeroVisual />
                </div>
            </Container>
        </section>
    )
}

export default HeroSection
